"use client";

import { IconCryptoWallet } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconArrowDownToLineOutline18, IconArrowUpRightOutline18, IconCheckOutline18, IconCopyOutline18 } from "nucleo-ui-outline-18";
import { TokenBalanceList } from "@/app/(v2)/wallet-management/_components/TokenBalanceList";
import { useChainInfoMap } from "@/app/(v2)/workflows/_hooks/useChainInfo";
import ResponsiveModal from "@/components/custom/ResponsiveModal";
import { Button } from "@/components/ui/button";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useSimBalance } from "@/hooks/useSimTokenInfo";
import { showToast } from "@/lib/toast";
import { getChainFamily, getDefaultDepositDestination } from "@/lib/utils/chain-family";
import { useOrganizationWalletModalStore } from "@/stores/organizationWalletModalStore";
import { useModalStore } from "@b3dotfun/sdk/global-account/react";
import Link from "next/link";
import { useMemo, useState } from "react";
function truncateAddress(address: string, start = 6, end = 4) {
  return `${address.slice(0, start)}...${address.slice(-end)}`;
}
export function OrganizationWalletDetailsModalHost() {
  const open = useOrganizationWalletModalStore(state => state.open);
  const wallet = useOrganizationWalletModalStore(state_0 => state_0.wallet);
  const close = useOrganizationWalletModalStore(state_1 => state_1.close);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const setModalOpen = useModalStore(state_2 => state_2.setB3ModalOpen);
  const setModalContentType = useModalStore(state_3 => state_3.setB3ModalContentType);
  const {
    data: balanceData,
    isLoading: isLoadingBalance,
    refetch: refetchBalance
  } = useSimBalance(wallet?.address);
  const totalUsd = useMemo(() => {
    if (!balanceData?.balances) return 0;
    return balanceData.balances.reduce((sum, balance) => sum + (balance.value_usd || 0), 0);
  }, [balanceData]);
  const formattedUsd = useMemo(() => {
    if (totalUsd >= 1000000) return `$${(totalUsd / 1000000).toFixed(2)}M`;
    if (totalUsd >= 1000) return `$${(totalUsd / 1000).toFixed(2)}K`;
    if (totalUsd >= 1) return `$${totalUsd.toFixed(2)}`;
    if (totalUsd > 0) return `$${totalUsd.toFixed(4)}`;
    return "$0.00";
  }, [totalUsd]);
  const chainIds = useMemo(() => {
    const ids = new Set<number>();
    balanceData?.balances?.forEach(balance_0 => ids.add(balance_0.chain_id));
    return [...ids];
  }, [balanceData]);
  const {
    chainInfoMap
  } = useChainInfoMap(chainIds);
  const copyAddress = async () => {
    if (!wallet) return;
    try {
      await navigator.clipboard.writeText(wallet.address);
      setCopiedAddress(true);
      showToast.success("Address copied");
      setTimeout(() => setCopiedAddress(false), 2000);
    } catch {
      showToast.error("Failed to copy address");
    }
  };
  const handleDeposit = () => {
    if (!wallet) return;
    if (getChainFamily(wallet.address) === "unknown") {
      showToast.error("Invalid wallet address");
      return;
    }
    const destination = getDefaultDepositDestination(wallet.address);
    setModalContentType({
      type: "anySpendDeposit",
      recipientAddress: wallet.address,
      destinationTokenAddress: destination.tokenAddress,
      destinationTokenChainId: destination.chainId,
      actionLabel: "Deposit to wallet",
      onSuccess: async () => {
        showToast.success("Deposit successful!");
        refetchBalance();
      },
      allowDirectTransfer: true,
      showFiatOption: false
    });
    setModalOpen(true);
  };
  if (!wallet) return null;
  return <ResponsiveModal open={open} onOpenChange={nextOpen => !nextOpen ? close() : undefined} title="Wallet Details">
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 flex h-12 w-12 items-center justify-center rounded-full">
            <IconCryptoWallet className="text-ds-text-subtle dark:text-ds-text-disabled h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-ds-text-primary text-lg font-semibold">{wallet.name}</h3>
            <div className="flex items-center gap-2">
              <p className="text-ds-text-muted font-mono text-sm">{truncateAddress(wallet.address, 10, 8)}</p>
              <button type="button" onClick={copyAddress} className="text-ds-text-disabled hover:bg-ds-surface-hover hover:text-ds-text-subtle dark:hover:bg-ds-neutral-800 rounded p-1 transition-colors dark:hover:text-zinc-300">
                {copiedAddress ? <IconCheckOutline18 className="h-3.5 w-3.5 text-green-600" /> : <IconCopyOutline18 className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
        </div>

        <div className="border-ds-border-default flex items-center justify-between rounded-lg border bg-zinc-50 p-4 dark:bg-zinc-800/50">
          <div>
            <p className="label label-xs">Total Balance</p>
            <p className="text-ds-text-primary mt-1 text-2xl font-bold">
              {isLoadingBalance ? <span className="text-ds-text-disabled flex items-center gap-2 text-base">
                  <PixelLoader size="sm" /> Loading...
                </span> : formattedUsd}
            </p>
          </div>
          <Button size="sm" onClick={handleDeposit}>
            <span className="flex items-center gap-1.5 px-2 text-xs">
              <IconArrowDownToLineOutline18 className="h-3.5 w-3.5" />
              Deposit
            </span>
          </Button>
        </div>

        {!isLoadingBalance && (balanceData?.balances?.length || 0) > 0 && <div className="space-y-3">
            <h4 className="label label-xs">Token Balances</h4>
            <div className="divide-ds-neutral-200 border-ds-border-default dark:divide-ds-neutral-700 max-h-64 divide-y overflow-y-auto rounded-2xl border">
              <TokenBalanceList balances={balanceData?.balances || []} isLoading={false} chainInfoMap={chainInfoMap} onTransferClick={() => {}} showTransferButton={false} />
            </div>
          </div>}

        <Button variant="white" asChild className="w-full">
          <Link href="/wallet-management" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
            <IconArrowUpRightOutline18 className="h-4 w-4" />
            Manage Wallets
          </Link>
        </Button>
      </div>
    </ResponsiveModal>;
}